import React, { PropsWithChildren, ReactNode } from 'react';

import { Link } from '~/lib/react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Divider, IconButton, Stack, Typography } from '@mui/material';

import useAppNavigation from '~/hooks/useAppNavigation';

type Props = PropsWithChildren<{
  title: ReactNode;
  subtitle?: ReactNode;
  backButton?: string | boolean;
  sticky?: boolean;
  bottomSpacing?: number;
  /** Rendered beside the title when no subtitle is set. */
  titleActions?: ReactNode;
}>;

/**
 * PageHeader - A generic page header component.
 *
 * For user-specific features (preferred agent, fraud warning), render those
 * directly in the page component rather than passing user data here.
 */
export default function PageHeader({
  title,
  children,
  subtitle,
  backButton,
  sticky = false,
  bottomSpacing = 3,
  titleActions,
}: Props) {
  const { goBack } = useAppNavigation();

  const backButtonProps = () => {
    if (typeof backButton === 'string') {
      return { to: backButton, component: Link };
    }

    return { onClick: goBack };
  };

  return (
    <Box
      pt={1}
      pb={bottomSpacing}
      sx={sticky ? { position: 'sticky', top: 0, zIndex: 1000, backgroundColor: 'background.default' } : undefined}
    >
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2} mb={1}>
        {backButton && (
          <IconButton sx={{ border: 1 }} size="small" color="secondary" title="Back" {...backButtonProps()}>
            <ArrowBackIcon />
          </IconButton>
        )}

        {!subtitle &&
          (titleActions ? (
            <>
              <Typography variant="h1">{title}</Typography>
              {titleActions}
              <Box flex={1} />
            </>
          ) : (
            <Typography flex={1} variant="h1">
              {title}
            </Typography>
          ))}

        {!!subtitle && (
          <Stack direction="column" gap={0} sx={{ flex: 1, minWidth: 0 }}>
            <Typography flex={1} variant="h1">
              {title}
            </Typography>
            <Typography variant="h2">{subtitle}</Typography>
          </Stack>
        )}

        {children && (
          <Box sx={{ alignSelf: { xs: 'stretch', sm: 'flex-end' }, width: { xs: '100%', sm: 'auto' } }}>{children}</Box>
        )}
      </Stack>

      <Divider />
    </Box>
  );
}
